// Bare email domains (no '@'). The '@' is prepended at match/display time
// to keep this list in lockstep with the backend Parameter Store format at
// /evop-inspector/{env}/app/auth-email-domains.
//
// These arrays are the BOOTSTRAP FALLBACK — used until `loadAllowedDomains()`
// resolves with the live list from `/api/auth/email-domains`. The backend
// remains the security authority; the frontend check is UX only.
export const INTERNAL_EMAIL_DOMAINS = ['poolwerx.com.au', 'poolwerx.com', 'poolwerx.co.nz'] as const
export const EXTERNAL_EMAIL_DOMAINS = [
  'accolution.com.au',
  'getvisory.com',
  'visory.com.au',
  'visorytech.com',
] as const
export const ALL_ALLOWED_DOMAINS = [
  ...INTERNAL_EMAIL_DOMAINS,
  ...EXTERNAL_EMAIL_DOMAINS,
] as const

// Bare registrable domains whose URLs may be rendered as links inside
// exception notes. BOOTSTRAP FALLBACK — replaced by the live list from
// `/api/auth/email-domains` once `loadAllowedDomains()` resolves. Mirrors the
// backend `OAuthService.FALLBACK_LINK_DOMAINS`.
export const FALLBACK_LINK_DOMAINS = [
  'xero.com',
  'lightspeed.app',
  'poolwerx.com',
  'poolwerx.com.au',
  'freshdesk.com',
  'getskimmer.com',
  'poolwerx.io',
  'pooltrackr.com',
  'docs.google.com',
  'drive.google.com',
] as const

// Mutable live cache — populated by `loadAllowedDomains()`.
let liveInternal: string[] | null = null
let liveExternal: string[] | null = null
let liveLinkDomains: string[] | null = null
let inFlight: Promise<void> | null = null

function currentInternal(): readonly string[] {
  return liveInternal ?? INTERNAL_EMAIL_DOMAINS
}

function currentAll(): readonly string[] {
  if (liveInternal && liveExternal) {
    return [...liveInternal, ...liveExternal]
  }
  return ALL_ALLOWED_DOMAINS
}

function emailMatchesDomain(email: string, domain: string): boolean {
  return email.toLowerCase().trim().endsWith(`@${domain.toLowerCase()}`)
}

export function isInternalEmailDomain(email: string): boolean {
  return currentInternal().some(d => emailMatchesDomain(email, d))
}

// Checks internal AND external domains — use for invitation-based flows.
export function isAllowedEmailDomain(email: string): boolean {
  return currentAll().some(d => emailMatchesDomain(email, d))
}

// Allowlist of domains whose URLs may be linkified in note bodies. Reflects
// the live list once `loadAllowedDomains()` resolves, else the bootstrap
// fallback. Used by `linkifyNoteBody` (display) and note-body link validation.
export function getLinkDomains(): readonly string[] {
  return liveLinkDomains ?? FALLBACK_LINK_DOMAINS
}

// Display helpers (prepend '@' for user-facing text). These are functions
// (not constants) so they reflect the live list once it's loaded.
export function getInternalDomainsDisplay(): string {
  return currentInternal().map(d => `@${d}`).join(' or ')
}

export function getAllDomainsDisplay(): string {
  return currentAll().map(d => `@${d}`).join(', ')
}

// Fetch the live allowlist from the backend. Call once at app bootstrap so
// that form validators reflect the source of truth in Parameter Store.
// Subsequent calls are no-ops while a fetch is in flight and safe to call
// from multiple places.
export async function loadAllowedDomains(): Promise<void> {
  if (inFlight) return inFlight
  inFlight = (async () => {
    try {
      const res = await fetch('/api/auth/email-domains', {
        credentials: 'same-origin',
      })
      if (!res.ok) return
      const data = await res.json()
      if (Array.isArray(data?.internal) && data.internal.length > 0) {
        liveInternal = data.internal.map((d: string) => String(d).toLowerCase())
      }
      if (Array.isArray(data?.external)) {
        liveExternal = data.external.map((d: string) => String(d).toLowerCase())
      }
      if (Array.isArray(data?.link_domains) && data.link_domains.length > 0) {
        liveLinkDomains = data.link_domains.map((d: string) => String(d).toLowerCase())
      }
    } catch {
      // Silently keep the bootstrap fallback; this is UX only.
    } finally {
      inFlight = null
    }
  })()
  return inFlight
}

// Kept for backwards compatibility. These reflect the BOOTSTRAP fallback
// only — new code should use the `get*DomainsDisplay()` helpers so the live
// allowlist from `loadAllowedDomains()` is picked up.
export const INTERNAL_DOMAINS_DISPLAY = INTERNAL_EMAIL_DOMAINS.map(d => `@${d}`).join(' or ')
export const ALL_DOMAINS_DISPLAY = ALL_ALLOWED_DOMAINS.map(d => `@${d}`).join(', ')
export const ALLOWED_DOMAINS_DISPLAY = ALL_DOMAINS_DISPLAY
